import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import CanvassMap from '../components/CanvassMap';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import { db } from '../firebase';
import {
  doc, setDoc, updateDoc, deleteDoc, addDoc, collection,
  onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { geocodeAddress, reverseGeocode } from '../utils/geocode';

// ─── Status metadata ────────────────────────────────────────────────────────

const CONTACT_OUTCOMES = [
  { value: 'pending',          label: 'Not started',      color: '#94a3b8' },
  { value: 'no_answer',        label: 'No answer',        color: '#f59e0b' },
  { value: 'declined',         label: 'Declined',         color: '#78716c' },
  { value: 'no_camera',        label: 'No camera',        color: '#64748b' },
  { value: 'footage_pending',  label: 'Footage pending',  color: '#3b82f6' },
  { value: 'footage_obtained', label: 'Footage obtained', color: '#6366f1' },
];
// Footage obtained splits into two colors once it's known whether the video was
// actually downloaded vs. just viewed on-scene — the plain CONTACT_OUTCOMES color
// above is the "viewed" shade. Kept out of the green family on purpose so
// "Subject seen" stays the only green in the whole color key.
const FOOTAGE_DOWNLOADED_COLOR = '#db2777';
const FLAG_COLOR = '#ea580c';
const CAMERA_TYPES = [
  { value: 'motion',     label: 'Motion' },
  { value: 'continuous', label: 'Continuous' },
];
const REVIEW_RESULTS = [
  { value: 'unreviewed',       label: 'Not reviewed',     color: '#6366f1' },
  { value: 'subject_seen',     label: 'Subject SEEN',     color: '#16a34a' },
  { value: 'subject_not_seen', label: 'Subject not seen', color: '#dc2626' },
];
const DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const DIR_DEG = { N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315 };

const labelOf = (list, v) => (list.find(x => x.value === v) || {}).label || v || '';

// timeSeen may be a Firestore Timestamp (new writes), a JS Date, or a legacy
// datetime-local string. Normalize any of them to a Date (or null).
function timeSeenToDate(v) {
  if (!v) return null;
  if (typeof v === 'string') { const d = new Date(v); return isNaN(d) ? null : d; }
  if (typeof v.toDate === 'function') return v.toDate();     // Firestore Timestamp
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  return null;
}

// Value for the <input type="datetime-local">: local "YYYY-MM-DDTHH:MM".
function toLocalInput(v) {
  const d = timeSeenToDate(v);
  if (!d) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Human-readable time-seen for the Locations list — "MM/DD/YY at H:MM AM/PM".
function fmtSeen(v) {
  const d = timeSeenToDate(v);
  if (!d) return '';
  const p = n => String(n).padStart(2, '0');
  const date = `${p(d.getMonth() + 1)}/${p(d.getDate())}/${p(d.getFullYear() % 100)}`;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} at ${time}`;
}

// Pin/dot color: a confirmed sighting result wins, else the contact outcome —
// with footage_obtained further split by whether it's been downloaded yet.
function pointColor(p) {
  if (p.reviewResult === 'subject_seen') return '#16a34a';
  if (p.reviewResult === 'subject_not_seen') return '#dc2626';
  if (p.contactOutcome === 'footage_obtained') return p.footageDownloaded ? FOOTAGE_DOWNLOADED_COLOR : '#6366f1';
  const c = CONTACT_OUTCOMES.find(o => o.value === p.contactOutcome);
  return c ? c.color : '#94a3b8';
}

// Reads an image file, downscales it via canvas so it stays well under
// Firestore's 1MiB/doc cap, and returns the shape stored on canvass/point docs.
function readAndDownscaleImage(file, maxEdge, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not read the image.'));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', quality), originalName: file.name, width, height });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
const arrowDegFor = p => (p.reviewResult === 'subject_seen' && p.direction ? DIR_DEG[p.direction] : null);

// Status buckets for the Locations list filter — mirrors the color taxonomy
// already used by the map pins and Legend, so "status" means the same thing
// everywhere in the tool.
const STATUS_BUCKETS = [
  { value: 'not_started', label: 'Not started / no answer', color: '#94a3b8' },
  { value: 'footage_pending', label: 'Footage pending/obtained', color: '#3b82f6' },
  { value: 'subject_seen', label: 'Subject seen', color: '#16a34a' },
  { value: 'subject_not_seen', label: 'Subject not seen', color: '#dc2626' },
  { value: 'no_camera', label: 'No camera / declined', color: '#64748b' },
];
function statusBucket(p) {
  if (p.reviewResult === 'subject_seen') return 'subject_seen';
  if (p.reviewResult === 'subject_not_seen') return 'subject_not_seen';
  if (p.contactOutcome === 'no_camera' || p.contactOutcome === 'declined') return 'no_camera';
  if (p.contactOutcome === 'footage_pending' || p.contactOutcome === 'footage_obtained') return 'footage_pending';
  return 'not_started';
}

// ─── Export ─────────────────────────────────────────────────────────────────
// All export work stays 100% client-side (no upload), consistent with the app's
// privacy guarantee. Both formats share the same column order.

// Static map image for the PDF, drawn 100% client-side: we fetch the same OSM
// tiles the live map uses (OSM sends `Access-Control-Allow-Origin: *`, so the
// canvas isn't tainted) and paint the pins on top. No pin data leaves the
// browser — only anonymous z/x/y tile requests, exactly as the on-screen map
// already makes.
const TILE = 256;
const lngToWorldX = (lng, z) => ((lng + 180) / 360) * TILE * 2 ** z;
const latToWorldY = (lat, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

function loadTileImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Returns a PNG data URL framed to the pins, or null (no pins, or tiles blocked
// — the PDF then just omits the map rather than failing).
async function renderCanvassMapImage(points, width = 1000, height = 560) {
  const pts = points.filter(p => p.lat != null && p.lng != null);
  if (!pts.length) return null;

  const lats = pts.map(p => +p.lat);
  const lngs = pts.map(p => +p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);

  // Largest zoom at which the padded bounds still fit the image.
  const pad = 1.3;
  let zoom = 1;
  for (let z = 18; z >= 1; z--) {
    const w = (lngToWorldX(maxLng, z) - lngToWorldX(minLng, z)) * pad;
    const h = (latToWorldY(minLat, z) - latToWorldY(maxLat, z)) * pad;
    if (w <= width && h <= height) { zoom = z; break; }
  }
  if (minLat === maxLat && minLng === maxLng) zoom = 16; // single point

  const centerX = (lngToWorldX(minLng, zoom) + lngToWorldX(maxLng, zoom)) / 2;
  const centerY = (latToWorldY(minLat, zoom) + latToWorldY(maxLat, zoom)) / 2;
  const originX = centerX - width / 2;
  const originY = centerY - height / 2;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  const n = 2 ** zoom;
  const subs = ['a', 'b', 'c'];
  const x0 = Math.floor(originX / TILE), x1 = Math.floor((originX + width) / TILE);
  const y0 = Math.floor(originY / TILE), y1 = Math.floor((originY + height) / TILE);
  const jobs = [];
  for (let tx = x0; tx <= x1; tx++) {
    for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n; // wrap around the antimeridian
      const s = subs[Math.abs(tx + ty) % subs.length];
      const url = `https://${s}.tile.openstreetmap.org/${zoom}/${wx}/${ty}.png`;
      const dx = tx * TILE - originX, dy = ty * TILE - originY;
      jobs.push(loadTileImage(url).then(img => ctx.drawImage(img, dx, dy)).catch(() => {}));
    }
  }
  await Promise.all(jobs);

  // Pins: colored dot + optional direction-of-travel arrow (mirrors CanvassMap).
  for (const p of pts) {
    const px = lngToWorldX(+p.lng, zoom) - originX;
    const py = latToWorldY(+p.lat, zoom) - originY;
    const color = pointColor(p);
    const deg = arrowDegFor(p);
    if (deg != null) {
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(0, 15); ctx.lineTo(0, -15);              // shaft (points "up" = north before rotation)
      ctx.moveTo(-7, -7); ctx.lineTo(0, -15); ctx.lineTo(7, -7); // arrowhead
      ctx.stroke();
      ctx.restore();
    }
    ctx.beginPath();
    ctx.arc(px, py, 7, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(px, py, 8.5, 0, 2 * Math.PI);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
  }

  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null; // canvas tainted (tiles blocked CORS) — omit the map gracefully
  }
}

const EXPORT_COLUMNS = [
  { header: '#',                key: 'num',        width: 5 },
  { header: 'Address',          key: 'address',    width: 40 },
  { header: 'Contact outcome',  key: 'outcome',     width: 18 },
  { header: 'Review result',    key: 'review',      width: 18 },
  { header: 'Direction',        key: 'dir',         width: 10 },
  { header: 'Video Offset',     key: 'offset',      width: 16 },
  { header: 'Camera Type',      key: 'camera',      width: 14 },
  { header: 'Video Time Frame', key: 'timeframe',   width: 20 },
  { header: 'Seen on video',    key: 'seen',        width: 22 },
  { header: 'Pulled by',        key: 'pulled',      width: 18 },
  { header: 'Needs Follow-up',  key: 'followup',     width: 14 },
  { header: 'Footage Downloaded', key: 'downloaded', width: 16 },
  { header: 'Photo',            key: 'photo',       width: 8 },
  { header: 'Notes',            key: 'notes',       width: 40 },
  { header: 'Latitude',         key: 'lat',         width: 12 },
  { header: 'Longitude',        key: 'lng',         width: 12 },
];

// Turn a stored point into the flat, human-readable row both exporters consume.
function exportRow(p, i) {
  return {
    num:     i + 1,
    address: p.address || `${(+p.lat).toFixed(5)}, ${(+p.lng).toFixed(5)}`,
    outcome: labelOf(CONTACT_OUTCOMES, p.contactOutcome),
    review:  p.contactOutcome === 'footage_obtained' ? labelOf(REVIEW_RESULTS, p.reviewResult) : '',
    dir:     p.reviewResult === 'subject_seen' && p.direction ? p.direction : '',
    offset:  p.videoOffset || '',
    camera:  labelOf(CAMERA_TYPES, p.cameraType),
    timeframe: p.videoTimeFrame || '',
    seen:    fmtSeen(p.timeSeen),
    pulled:  p.pulledBy || '',
    followup: p.needsFollowUp ? 'Yes' : '',
    downloaded: p.contactOutcome === 'footage_obtained' ? (p.footageDownloaded ? 'Yes' : 'No') : '',
    photo:   p.photo ? 'Yes' : '',
    notes:   p.notes || '',
    lat:     p.lat != null ? +(+p.lat).toFixed(6) : '',
    lng:     p.lng != null ? +(+p.lng).toFixed(6) : '',
  };
}

const safeFileName = s =>
  (s || 'video-canvass').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'video-canvass';

async function exportCanvassExcel(meta, points) {
  const { default: ExcelJS } = await import('exceljs');
  // file-saver's module.exports is the saveAs function itself (with a .saveAs
  // property); webpack doesn't always synthesize the named export, so fall back.
  const fileSaver = await import('file-saver');
  const saveAs = fileSaver.saveAs || fileSaver.default || fileSaver;
  const DARK = 'FF0F172A', ALT = 'FFEEF2FF', WHITE = 'FFFFFFFF';

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Locations');

  ws.getCell('A1').value = meta.title || 'Video Canvass';
  ws.getCell('A1').font = { bold: true, size: 14 };
  const seen = points.filter(p => p.reviewResult === 'subject_seen').length;
  ws.getCell('A2').value =
    `${points.length} location${points.length !== 1 ? 's' : ''} · ${seen} with subject seen` +
    (meta.subjectDesc ? ` · Subject: ${meta.subjectDesc}` : '') +
    (meta.extraInfo ? ` · ${meta.extraInfo}` : '') +
    ` · Exported ${new Date().toLocaleString()}`;
  ws.getCell('A2').font = { color: { argb: 'FF64748B' } };

  const headerRowNum = 4;
  ws.getRow(headerRowNum).values = EXPORT_COLUMNS.map(c => c.header);
  ws.getRow(headerRowNum).eachCell(c => {
    c.font = { bold: true, color: { argb: WHITE } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
    c.alignment = { horizontal: 'center' };
  });

  points.forEach((p, i) => {
    const r = exportRow(p, i);
    const row = ws.addRow(EXPORT_COLUMNS.map(c => r[c.key]));
    if (i % 2 === 1) row.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT } }; });
    row.getCell(EXPORT_COLUMNS.findIndex(c => c.key === 'notes') + 1).alignment = { wrapText: true, vertical: 'top' };
  });

  EXPORT_COLUMNS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });
  ws.views = [{ state: 'frozen', ySplit: headerRowNum }];

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${safeFileName(meta.title)}.xlsx`,
  );
}

async function exportCanvassPdf(meta, points) {
  const { default: pdfMake } = await import('pdfmake/build/pdfmake');
  // This build of vfs_fonts exports the font map directly (module.exports = vfs),
  // so the font map is the module's default export, not a `.vfs` property.
  const pdfFonts = await import('pdfmake/build/vfs_fonts');
  pdfMake.vfs = pdfFonts.default || pdfFonts;

  // Snapshot of the map framed to the pins (null if there are none or tiles fail).
  const mapImage = await renderCanvassMapImage(points).catch(() => null);

  const seen = points.filter(p => p.reviewResult === 'subject_seen').length;
  // Compact set of columns for the (landscape) page; the Excel export carries the full detail.
  const cols = [
    { header: '#',              key: 'num',        width: 14 },
    { header: 'Address',        key: 'address',    width: '*' },
    { header: 'Contact',        key: 'outcome',     width: 55 },
    { header: 'Review',         key: 'review',      width: 50 },
    { header: 'Dir',            key: 'dir',         width: 18 },
    { header: 'Offset',         key: 'offset',      width: 48 },
    { header: 'Camera',         key: 'camera',      width: 40 },
    { header: 'Video window',   key: 'timeframe',   width: 65 },
    { header: 'Seen on video',  key: 'seen',        width: 75 },
    { header: 'Pulled by',      key: 'pulled',      width: 45 },
    { header: 'Follow-up',      key: 'followup',    width: 38 },
    { header: 'Downloaded',     key: 'downloaded',  width: 42 },
    { header: 'Photo',          key: 'photo',       width: 32 },
    { header: 'Notes',          key: 'notes',       width: 80 },
  ];

  // Compute each row once (keyed by its position in the full list) so the
  // timeline below can reference the same row numbers as the full table.
  const allRows = points.map((p, i) => ({ p, row: exportRow(p, i) }));

  const headerCells = cols.map(c => ({ text: c.header, bold: true, color: '#ffffff', fillColor: '#0f172a', margin: [0, 3, 0, 3] }));
  const bodyRows = allRows.map(({ row }, i) =>
    cols.map(c => ({ text: String(row[c.key] ?? ''), fillColor: i % 2 ? '#eef2ff' : null, margin: [0, 2, 0, 2] })));

  // Subject Sighting Timeline: only "subject seen" locations, in chronological
  // order of when the subject was seen on video (locations with no recorded
  // time sort last rather than misleadingly first). Row numbers match the
  // full table below so the two cross-reference each other.
  const timelineCols = [
    { header: '#',          key: 'num',     width: 20 },
    { header: 'Time seen',  key: 'seen',    width: 110 },
    { header: 'Address',    key: 'address', width: '*' },
    { header: 'Heading',    key: 'dir',     width: 55 },
    { header: 'Pulled by',  key: 'pulled',  width: 80 },
    { header: 'Notes',      key: 'notes',   width: 160 },
  ];
  const seenRows = allRows
    .filter(({ p }) => p.reviewResult === 'subject_seen')
    .sort((a, b) => {
      const av = +timeSeenToDate(a.p.timeSeen) || 0, bv = +timeSeenToDate(b.p.timeSeen) || 0;
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return av - bv;
    });
  const timelineSection = seenRows.length === 0 ? null : [
    { text: 'Subject Sighting Timeline', fontSize: 12, bold: true, color: '#15803d', margin: [0, 4, 0, 6] },
    {
      table: {
        headerRows: 1,
        widths: timelineCols.map(c => c.width),
        body: [
          timelineCols.map(c => ({ text: c.header, bold: true, color: '#ffffff', fillColor: '#16a34a', margin: [0, 3, 0, 3] })),
          ...seenRows.map(({ row }, i) => timelineCols.map(c => ({ text: String(row[c.key] ?? ''), fillColor: i % 2 ? '#f0fdf4' : null, margin: [0, 2, 0, 2] }))),
        ],
      },
      layout: {
        hLineWidth: () => 0.5, vLineWidth: () => 0.5,
        hLineColor: () => '#bbf7d0', vLineColor: () => '#bbf7d0',
      },
      margin: [0, 0, 0, 14],
    },
  ];

  const allLocationsSection = points.length === 0
    ? [{ text: 'No locations recorded yet.', italics: true, color: '#94a3b8' }]
    : [
        { text: 'All Locations', fontSize: 12, bold: true, color: '#0f172a', margin: [0, 4, 0, 6] },
        {
          table: { headerRows: 1, widths: cols.map(c => c.width), body: [headerCells, ...bodyRows] },
          layout: {
            hLineWidth: () => 0.5, vLineWidth: () => 0.5,
            hLineColor: () => '#e2e8f0', vLineColor: () => '#e2e8f0',
          },
        },
      ];

  const docDefinition = {
    pageOrientation: 'landscape',
    pageMargins: [28, 36, 28, 36],
    defaultStyle: { fontSize: 8 },
    content: [
      { text: meta.title || 'Video Canvass', fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
      meta.subjectDesc ? { text: `Subject: ${meta.subjectDesc}`, color: '#475569', margin: [0, 0, 0, 2] } : null,
      meta.extraInfo ? { text: meta.extraInfo, color: '#64748b', fontSize: 8, margin: [0, 0, 0, 2] } : null,
      { text: `${points.length} location${points.length !== 1 ? 's' : ''} · ${seen} with subject seen · Exported ${new Date().toLocaleString()}`,
        color: '#64748b', fontSize: 8, margin: [0, 0, 0, 10] },
      meta.photo?.dataUrl ? { image: meta.photo.dataUrl, width: 110, margin: [0, 0, 0, 10] } : null,
      mapImage ? { image: mapImage, width: 785, margin: [0, 0, 0, 12] } : null,
      ...(timelineSection || []),
      ...allLocationsSection,
    ].filter(Boolean),
  };

  pdfMake.createPdf(docDefinition).download(`${safeFileName(meta.title)}.pdf`);
}

// 22-char capability id (~131 bits) — the whole access model rests on this being
// unguessable, so we don't use the shorter Firestore auto-id.
function makeId(len = 22) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  let s = '';
  for (let i = 0; i < len; i++) s += chars[arr[i] % chars.length];
  return s;
}

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 13 };
const emptyDraft = extra => ({
  address: '', pulledBy: localStorage.getItem('canvass_pulledBy') || '',
  contactOutcome: 'pending', reviewResult: 'unreviewed',
  direction: '', timeSeen: '', notes: '',
  videoTimeFrame: '', videoOffset: '', needsFollowUp: false, footageDownloaded: false,
  // cameraType and photo are left absent rather than defaulted — cameraType is
  // Firestore-rules-enum-constrained when present, so "unset" must mean the
  // key is missing, not empty; photo similarly must be a real map or absent.
  ...extra,
});

// ─── Shared banner ──────────────────────────────────────────────────────────

function SharedBanner() {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25,
      bgcolor: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3',
      borderRadius: '10px', p: '12px 16px', mb: 2.5, fontSize: '13px', lineHeight: 1.5,
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ flexShrink: 0, marginTop: '1px' }}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      <span>
        <strong>Shared, cloud-backed tool.</strong> Unlike the rest of LE Tech Tools, a canvass is stored
        online so your whole team sees updates live, and anyone with the link can view and edit. Keep the link inside your
        team. 
      </span>
    </Box>
  );
}

// Optional still-photo upload + thumbnail + remove, shared by the canvass-level
// and point-level photo fields (they only differ in downscale target size).
function PhotoUploadField({ label, photo, onChange, maxEdge, quality = 0.85 }) {
  const [busy, setBusy] = useState(false);
  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onChange(await readAndDownscaleImage(file, maxEdge, quality));
    } catch (err) {
      alert(`Could not read that image: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Box>
      {photo?.dataUrl && (
        <Box sx={{ mb: 1 }}>
          <img src={photo.dataUrl} alt={label} style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, display: 'block' }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button component="label" size="small" variant="outlined" disabled={busy}
          sx={{ borderColor: '#6d28d9', color: '#6d28d9', fontWeight: 600 }}>
          {busy ? 'Processing…' : photo?.dataUrl ? 'Replace photo' : `Upload ${label}`}
          <input type="file" accept="image/*" hidden onChange={onFile} />
        </Button>
        {photo?.dataUrl && (
          <Button size="small" onClick={() => onChange(null)} sx={{ color: '#dc2626' }}>Remove</Button>
        )}
      </Box>
    </Box>
  );
}

// ─── Create / landing view ──────────────────────────────────────────────────

function CreateCanvass() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [subjectDesc, setSubjectDesc] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [joinVal, setJoinVal] = useState('');

  async function create() {
    if (busy) return;
    setBusy(true);
    // Best-effort: start the map on the creator's location if they allow it.
    const center = await new Promise(resolve => {
      if (!navigator.geolocation) return resolve(DEFAULT_CENTER);
      const done = c => resolve(c);
      const timer = setTimeout(() => done(DEFAULT_CENTER), 4000);
      navigator.geolocation.getCurrentPosition(
        pos => { clearTimeout(timer); done({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 15 }); },
        () => { clearTimeout(timer); done(DEFAULT_CENTER); },
        { timeout: 4000 },
      );
    });
    try {
      const id = makeId();
      const payload = {
        title: title.trim() || 'Untitled canvass',
        subjectDesc: subjectDesc.trim(),
        center,
        createdAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
      };
      if (extraInfo.trim()) payload.extraInfo = extraInfo.trim();
      if (photo) payload.photo = photo;
      await setDoc(doc(db, 'canvasses', id), payload);
      navigate(`/canvass/${id}`);
    } catch (e) {
      setBusy(false);
      alert(`Could not create the canvass: ${e.message}`);
    }
  }

  function join() {
    const v = joinVal.trim();
    if (!v) return;
    const m = v.match(/canvass\/([A-Za-z0-9]+)/);
    navigate(`/canvass/${m ? m[1] : v}`);
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
          Video Canvass Tracker
        </Typography>
        <Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
          Create a shared map so a team pulling surveillance video can see, in real time, which
          addresses have been hit, what was found, and which way the subject went.
        </Typography>
      </Box>

      <SharedBanner />

	  <Paper elevation={0} sx={{
        borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '24px',
      }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>Open an existing canvass</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth size="small" label="Paste team link or ID"
            value={joinVal} onChange={e => setJoinVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') join(); }}
          />
          <Button variant="outlined" onClick={join}
            sx={{ borderColor: '#6d28d9', color: '#6d28d9', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Open
          </Button>
        </Box>
      </Paper>
	  <br/>

      <Paper elevation={0} sx={{
        borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
        p: '24px', mb: 2.5,
      }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>Start a new canvass</Typography>
        <TextField
          fullWidth size="small" label="Canvass name" placeholder="e.g. Elm St canvass — 6/28 shooting"
          value={title} onChange={e => setTitle(e.target.value)} sx={{ mb: 2 }}
        />
        <TextField
          fullWidth size="small" label="Subject description (optional)"
          placeholder="e.g. male, red hoodie, last seen on foot NB"
          value={subjectDesc} onChange={e => setSubjectDesc(e.target.value)} sx={{ mb: 2 }}
        />
        <TextField
          fullWidth size="small" label="Additional info (optional)" multiline minRows={2}
          placeholder="e.g. initial search area, initial time, location of the crime"
          value={extraInfo} onChange={e => setExtraInfo(e.target.value)} sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>Photo (optional)</Typography>
          <PhotoUploadField label="photo" photo={photo} onChange={setPhoto} maxEdge={1400} quality={0.85} />
        </Box>
        <Button
          variant="contained" fullWidth onClick={create} disabled={busy}
          sx={{ p: '10px', bgcolor: '#6d28d9', fontWeight: 600, borderRadius: '8px',
            '&:hover': { bgcolor: '#5b21b6' }, '&:disabled': { bgcolor: '#94a3b8' } }}
        >
          {busy ? 'Creating…' : 'Create canvass & get shareable link'}
        </Button>
      </Paper>

      
    </Container>
  );
}

// ─── Board view ─────────────────────────────────────────────────────────────

function CanvassBoard({ canvassId }) {
  const [meta, setMeta] = useState(undefined); // undefined=loading, null=not found
  const [points, setPoints] = useState([]);
  const [draft, setDraft] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [center, setCenter] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [geoResults, setGeoResults] = useState([]);
  const [geoBusy, setGeoBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pointsLoaded, setPointsLoaded] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [listStatus, setListStatus] = useState('all');
  const [listSort, setListSort] = useState('added');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [editOpenTick, setEditOpenTick] = useState(0);
  const centerInit = useRef(false);
  const editorRef = useRef(null);

  const canvassRef = useMemo(() => doc(db, 'canvasses', canvassId), [canvassId]);
  const pointsCol = useMemo(() => collection(db, 'canvasses', canvassId, 'points'), [canvassId]);

  // Live subscriptions.
  useEffect(() => {
    const unsub = onSnapshot(canvassRef,
      snap => setMeta(snap.exists() ? snap.data() : null),
      () => setMeta(null));
    return unsub;
  }, [canvassRef]);

  useEffect(() => {
    const unsub = onSnapshot(query(pointsCol, orderBy('createdAt', 'asc')),
      snap => { setPoints(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setPointsLoaded(true); },
      () => setPointsLoaded(true));
    return unsub;
  }, [pointsCol]);

  // On first load, focus the map on the pins that have already been placed, so a
  // shared link opens on the canvass activity rather than the creation spot. Only
  // fall back to the stored creation center when there are no pins yet. We wait for
  // the points snapshot (pointsLoaded) so we don't lock onto the creation center
  // before the existing pins have arrived; leaving center null lets CanvassMap
  // fit its bounds to all pins.
  useEffect(() => {
    if (centerInit.current || !meta || !pointsLoaded) return;
    centerInit.current = true;
    if (points.length === 0 && meta.center) setCenter(meta.center);
  }, [meta, pointsLoaded, points]);

  // Scroll the edit form into view whenever it opens — otherwise, picking a
  // location far down a long list silently switches to edit mode off-screen.
  useEffect(() => {
    if (editOpenTick && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editOpenTick]);

  const mapPoints = useMemo(() => points.map(p => ({
    id: p.id, lat: p.lat, lng: p.lng,
    color: pointColor(p), arrowDeg: arrowDegFor(p), flagged: !!p.needsFollowUp,
    label: p.address || p.pulledBy || '',
  })), [points]);

  // Locations-list search, status filter, flagged filter, and sort. Only the
  // list is affected — the map keeps showing every pin so the team never loses
  // track of one.
  const visiblePoints = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    let list = points.filter(p => {
      if (listStatus !== 'all' && statusBucket(p) !== listStatus) return false;
      if (flaggedOnly && !p.needsFollowUp) return false;
      if (!q) return true;
      const hay = `${p.address || ''} ${p.notes || ''} ${p.pulledBy || ''}`.toLowerCase();
      return hay.includes(q);
    });
    const dateVal = v => +timeSeenToDate(v) || 0;
    if (listSort === 'address') {
      list = [...list].sort((a, b) => (a.address || '').localeCompare(b.address || ''));
    } else if (listSort === 'status') {
      const rank = Object.fromEntries(STATUS_BUCKETS.map((b, i) => [b.value, i]));
      list = [...list].sort((a, b) => rank[statusBucket(a)] - rank[statusBucket(b)]);
    } else if (listSort === 'updated') {
      list = [...list].sort((a, b) => dateVal(b.updatedAt) - dateVal(a.updatedAt));
    } else if (listSort === 'newest') {
      list = [...list].sort((a, b) => dateVal(b.createdAt) - dateVal(a.createdAt));
    } else if (listSort === 'seen') {
      // Chronological by sighting time, so the team can reconstruct the
      // subject's path in order; locations with no sighting time sort last
      // regardless — not to epoch-zero, which would misleadingly put them first.
      list = [...list].sort((a, b) => {
        const av = dateVal(a.timeSeen), bv = dateVal(b.timeSeen);
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return av - bv;
      });
    }
    // 'added' (default) keeps the Firestore query's insertion order as-is.
    return list;
  }, [points, listSearch, listStatus, listSort, flaggedOnly]);

  const touchActivity = () => updateDoc(canvassRef, { lastActivityAt: serverTimestamp() }).catch(() => {});

  // ── Add-location entry points ──
  async function dropAt(lat, lng, address = '') {
    setAddMode(false);
    setDraft(emptyDraft({ lat, lng, address }));
    setCenter({ lat, lng, zoom: 16 });
    if (!address) {
      const label = await reverseGeocode(lat, lng);
      if (label) setDraft(d => (d && !d.id && !d.address ? { ...d, address: label } : d));
    }
  }
  function useMyLocation() {
    if (!navigator.geolocation) return alert('Location is not available in this browser.');
    navigator.geolocation.getCurrentPosition(
      pos => dropAt(pos.coords.latitude, pos.coords.longitude),
      () => alert('Could not get your location.'),
    );
  }
  async function runGeocode() {
    const q = addressQuery.trim();
    if (!q) return;
    setGeoBusy(true);
    try {
      setGeoResults(await geocodeAddress(q));
    } catch (e) {
      alert(`Address lookup failed: ${e.message}`);
    } finally {
      setGeoBusy(false);
    }
  }

  // ── Persist ──
  async function saveDraft() {
    trackToolAction('/canvass', 'Video Canvass Tracker');
    if (!draft || draft.lat == null) return;
    const { id, createdAt, updatedAt, ...fields } = draft; // eslint-disable-line no-unused-vars
    if (fields.pulledBy) localStorage.setItem('canvass_pulledBy', fields.pulledBy);
    // Store timeSeen as a real timestamp (or explicit null), not a raw string,
    // so it lands in Firestore as a Timestamp and is sortable/queryable.
    const seen = timeSeenToDate(fields.timeSeen);
    const payload = { ...fields, timeSeen: seen };
    try {
      if (id) {
        await updateDoc(doc(pointsCol, id), { ...payload, updatedAt: serverTimestamp() });
      } else {
        await addDoc(pointsCol, { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      }
      touchActivity();
      setDraft(null);
    } catch (e) {
      alert(`Could not save: ${e.message}`);
    }
  }
  async function removePoint(id) {
    try { await deleteDoc(doc(pointsCol, id)); touchActivity(); setDraft(null); }
    catch (e) { alert(`Could not delete: ${e.message}`); }
  }
  function moveExisting(id, lat, lng) {
    updateDoc(doc(pointsCol, id), { lat, lng, updatedAt: serverTimestamp() }).catch(() => {});
  }

  function editPoint(p) {
    // Normalize the stored timestamp back to the datetime-local input string.
    setDraft({ ...p, timeSeen: toLocalInput(p.timeSeen) });
    setFocusId(p.id);
    setCenter({ lat: p.lat, lng: p.lng, zoom: 16 });
    setEditOpenTick(t => t + 1);
  }

  function copyLink() {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
      .catch(() => {});
  }

  async function runExport(kind) {
    setExportAnchor(null);
    setExporting(true);
    try {
      if (kind === 'pdf') await exportCanvassPdf(meta, points);
      else await exportCanvassExcel(meta, points);
    } catch (e) {
      alert(`Could not export: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  if (meta === undefined) {
    return <Container maxWidth="md" sx={{ py: 6 }}><Typography sx={{ color: '#64748b' }}>Loading canvass…</Typography></Container>;
  }
  if (meta === null) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ borderRadius: '14px', p: '28px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,.07)' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>Canvass not found</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '14px', mb: 2 }}>
            This link may be mistyped, or the canvass was deleted.
          </Typography>
          <Button component={Link} to="/canvass" variant="contained" sx={{ bgcolor: '#6d28d9', '&:hover': { bgcolor: '#5b21b6' } }}>
            Start a new canvass
          </Button>
        </Paper>
      </Container>
    );
  }

  const seen = points.filter(p => p.reviewResult === 'subject_seen').length;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>{meta.title}</Typography>
          {meta.subjectDesc && <Typography sx={{ color: '#64748b', mt: '4px', fontSize: '14px' }}>Subject: {meta.subjectDesc}</Typography>}
          {meta.extraInfo && <Typography sx={{ color: '#64748b', mt: '4px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{meta.extraInfo}</Typography>}
          {meta.photo?.dataUrl && (
            <Box sx={{ mt: 1 }}>
              <Box component="img" src={meta.photo.dataUrl} alt="Subject / scene" onClick={() => setLightboxPhoto(meta.photo.dataUrl)}
                sx={{ maxWidth: 180, maxHeight: 180, borderRadius: '8px', display: 'block', cursor: 'zoom-in' }} />
            </Box>
          )}
          <Typography sx={{ color: '#94a3b8', mt: '4px', fontSize: '13px' }}>
            {points.length} location{points.length !== 1 ? 's' : ''} · {seen} with subject seen
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={copyLink} variant="contained"
            sx={{ bgcolor: copied ? '#16a34a' : '#6d28d9', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap',
              '&:hover': { bgcolor: copied ? '#16a34a' : '#5b21b6' } }}>
            {copied ? 'Link copied ✓' : 'Copy team link'}
          </Button>
          <Button onClick={e => setExportAnchor(e.currentTarget)} variant="outlined"
            disabled={exporting || points.length === 0}
            sx={{ borderColor: '#6d28d9', color: '#6d28d9', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#5b21b6', bgcolor: '#f5f3ff' } }}>
            {exporting ? 'Exporting…' : 'Export ▾'}
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => runExport('pdf')}>Export as PDF</MenuItem>
            <MenuItem onClick={() => runExport('excel')}>Export as Excel (.xlsx)</MenuItem>
          </Menu>
        </Box>
      </Box>

      <SharedBanner />

      {/* Toolbar */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '16px', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <Button onClick={() => { setAddMode(a => !a); setDraft(null); }} variant={addMode ? 'contained' : 'outlined'}
            sx={{ fontWeight: 600, borderRadius: '8px',
              ...(addMode ? { bgcolor: '#6d28d9', '&:hover': { bgcolor: '#5b21b6' } } : { borderColor: '#6d28d9', color: '#6d28d9' }) }}>
            {addMode ? 'Tap the map to drop a pin…' : '+ Add by tapping map'}
          </Button>
          <Button onClick={useMyLocation} variant="outlined" sx={{ borderColor: '#cbd5e1', color: '#334155', fontWeight: 600, borderRadius: '8px' }}>
            Use my location
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField fullWidth size="small" placeholder="…or search an address"
            value={addressQuery}
            onChange={e => setAddressQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runGeocode(); }} />
          <Button onClick={runGeocode} disabled={geoBusy} variant="outlined"
            sx={{ borderColor: '#6d28d9', color: '#6d28d9', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {geoBusy ? 'Searching…' : 'Find'}
          </Button>
        </Box>
        {geoResults.length > 0 && (
          <Box sx={{ mt: 1, border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            {geoResults.map((r, i) => (
              <Box key={i} onClick={() => { dropAt(r.lat, r.lng, r.label); setGeoResults([]); setAddressQuery(''); }}
                sx={{ p: '8px 12px', fontSize: '13px', cursor: 'pointer', borderBottom: i < geoResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                  '&:hover': { bgcolor: '#f5f3ff' } }}>
                {r.label}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Map */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '12px', mb: 2 }}>
        <CanvassMap
          points={mapPoints}
          center={center}
          pending={draft && !draft.id ? { lat: draft.lat, lng: draft.lng } : null}
          addMode={addMode}
          focusId={focusId}
          onMapClick={dropAt}
          onMarkerClick={id => { const p = points.find(x => x.id === id); if (p) editPoint(p); }}
          onMarkerDragEnd={(id, lat, lng) => (draft && draft.id === id ? setDraft(d => ({ ...d, lat, lng })) : moveExisting(id, lat, lng))}
          onPendingDragEnd={(lat, lng) => setDraft(d => (d ? { ...d, lat, lng } : d))}
        />
        <Legend />
      </Paper>

      {/* Editor */}
      {draft && (
        <Box ref={editorRef}>
          <PointEditor
            draft={draft} setDraft={setDraft}
            onSave={saveDraft} onCancel={() => setDraft(null)}
            onDelete={draft.id ? () => removePoint(draft.id) : null}
          />
        </Box>
      )}

      {/* List */}
      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Locations</Typography>
          {points.length > 0 && (
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
              Showing {visiblePoints.length} of {points.length}
            </Typography>
          )}
        </Box>

        {points.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <TextField size="small" placeholder="Search address, notes, pulled by…"
              value={listSearch} onChange={e => setListSearch(e.target.value)}
              sx={{ flex: '1 1 200px', minWidth: 160 }} />
            <TextField select size="small" label="Status" value={listStatus} onChange={e => setListStatus(e.target.value)}
              sx={{ minWidth: 160 }}>
              <MenuItem value="all">All statuses</MenuItem>
              {STATUS_BUCKETS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Sort" value={listSort} onChange={e => setListSort(e.target.value)}
              sx={{ minWidth: 140 }}>
              <MenuItem value="added">Order added</MenuItem>
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="updated">Recently updated</MenuItem>
              <MenuItem value="seen">Time seen on video</MenuItem>
              <MenuItem value="address">Address (A–Z)</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Checkbox size="small" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)}
                sx={{ color: FLAG_COLOR, '&.Mui-checked': { color: FLAG_COLOR } }} />}
              label={<Typography sx={{ fontSize: '13px', color: '#475569' }}>Needs follow-up only</Typography>}
            />
          </Box>
        )}

        {points.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>No locations yet — add the first one above.</Typography>
        ) : visiblePoints.length === 0 ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>No locations match your search/filter.</Typography>
        ) : visiblePoints.map(p => (
          <Box key={p.id} onClick={() => editPoint(p)}
            sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: '10px 8px', borderTop: '1px solid #f1f5f9', cursor: 'pointer',
              '&:hover': { bgcolor: '#f8fafc' } }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: pointColor(p), mt: '3px', flexShrink: 0, border: '2px solid #fff', boxShadow: `0 0 0 1.5px ${pointColor(p)}` }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {p.address || `${(+p.lat).toFixed(5)}, ${(+p.lng).toFixed(5)}`}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '1px' }}>
                {labelOf(CONTACT_OUTCOMES, p.contactOutcome)}
                {p.contactOutcome === 'footage_obtained' && ` · ${labelOf(REVIEW_RESULTS, p.reviewResult)}`}
                {p.contactOutcome === 'footage_obtained' && p.footageDownloaded && ' · downloaded'}
                {p.reviewResult === 'subject_seen' && p.direction && ` · heading ${p.direction}`}
                {p.timeSeen && ` · seen on video ${fmtSeen(p.timeSeen)}`}
                {p.pulledBy && ` · ${p.pulledBy}`}
              </Typography>
              {!!p.needsFollowUp && (
                <Typography sx={{ fontSize: '11px', color: FLAG_COLOR, fontWeight: 700, mt: '1px' }}>NEEDS FOLLOW-UP</Typography>
              )}
              {p.notes && <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '1px' }}>{p.notes}</Typography>}
            </Box>
            {p.photo?.dataUrl && (
              <Box component="img" src={p.photo.dataUrl} alt="" onClick={e => { e.stopPropagation(); setLightboxPhoto(p.photo.dataUrl); }}
                sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0,
                  border: '1px solid #e2e8f0', cursor: 'zoom-in' }} />
            )}
          </Box>
        ))}
      </Paper>

      <Dialog open={!!lightboxPhoto} onClose={() => setLightboxPhoto(null)} maxWidth={false}>
        {lightboxPhoto && (
          <Box component="img" src={lightboxPhoto} alt="" onClick={() => setLightboxPhoto(null)}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh', cursor: 'zoom-out' }} />
        )}
      </Dialog>
    </Container>
  );
}

// Pulled straight from CONTACT_OUTCOMES/REVIEW_RESULTS so the legend can never
// drift out of sync with pointColor()'s actual precedence.
const LEGEND_ITEMS = [
  ...CONTACT_OUTCOMES.filter(o => o.value !== 'footage_obtained').map(o => ({ label: o.label, color: o.color })),
  { label: 'Footage obtained (viewed)', color: '#6366f1' },
  { label: 'Footage downloaded', color: FOOTAGE_DOWNLOADED_COLOR },
  { label: 'Subject seen', color: '#16a34a' },
  { label: 'Subject not seen', color: '#dc2626' },
];

function Legend() {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', mt: 1.25, px: '4px' }}>
      {LEGEND_ITEMS.map(b => (
        <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.color }} />
          <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{b.label}</Typography>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: `2px dashed ${FLAG_COLOR}` }} />
        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>Needs follow-up</Typography>
      </Box>
      <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>Arrow = direction of travel · drag a pin to reposition (confirms first)</Typography>
    </Box>
  );
}

function PointEditor({ draft, setDraft, onSave, onCancel, onDelete }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  // Enum-constrained optional fields (validated by firestore.rules as
  // in ['x','y'] when present) must be *removed*, not set to '', when cleared —
  // an empty string would fail validation, unlike the free-text fields above.
  const toggleValue = (k, v) => setDraft(d => {
    const nd = { ...d };
    if (nd[k] === v) delete nd[k]; else nd[k] = v;
    return nd;
  });
  const setPointPhoto = photo => setDraft(d => {
    const nd = { ...d };
    if (photo) nd.photo = photo; else delete nd.photo;
    return nd;
  });
  const showReview = draft.contactOutcome === 'footage_obtained';
  const showSeenDetails = showReview && draft.reviewResult === 'subject_seen';
  return (
    <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '20px', mb: 2, border: '1.5px solid #ddd6fe' }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>
        {draft.id ? 'Edit location' : 'New location'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField size="small" label="Address / place" value={draft.address || ''} onChange={e => set('address', e.target.value)} sx={{ gridColumn: { sm: '1 / -1' } }} />
        <TextField size="small" label="Pulled by (initials)" value={draft.pulledBy || ''} onChange={e => set('pulledBy', e.target.value)} />
        <TextField select size="small" label="Contact outcome" value={draft.contactOutcome} onChange={e => set('contactOutcome', e.target.value)}>
          {CONTACT_OUTCOMES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {showReview && (
          <TextField select size="small" label="Review result" value={draft.reviewResult} onChange={e => set('reviewResult', e.target.value)}>
            {REVIEW_RESULTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
        <TextField size="small" label="Time frame of video pulled (optional)" placeholder="e.g. 14:00–16:30"
          value={draft.videoTimeFrame || ''} onChange={e => set('videoTimeFrame', e.target.value)} />
        {showSeenDetails && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField size="small" type="datetime-local" label="Time seen on video" InputLabelProps={{ shrink: true }}
              value={draft.timeSeen || ''} onChange={e => set('timeSeen', e.target.value)} sx={{ flex: 1 }} />
            <Button size="small" onClick={() => set('timeSeen', toLocalInput(new Date()))}
              sx={{ color: '#6d28d9', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 'auto' }}>
              Now
            </Button>
          </Box>
        )}
      </Box>

      {showSeenDetails && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>Direction of travel</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {DIRECTIONS.map(d => (
              <Box key={d} component="button" type="button" onClick={() => set('direction', draft.direction === d ? '' : d)}
                sx={{ cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '13px',
                  px: '12px', py: '6px', borderRadius: '8px', border: '1px solid',
                  ...(draft.direction === d
                    ? { bgcolor: '#6d28d9', color: '#fff', borderColor: '#6d28d9' }
                    : { bgcolor: '#fff', color: '#475569', borderColor: '#cbd5e1' }) }}>
                {d}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>Camera type (optional)</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {CAMERA_TYPES.map(o => (
            <Box key={o.value} component="button" type="button" onClick={() => toggleValue('cameraType', o.value)}
              sx={{ cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '13px',
                px: '12px', py: '6px', borderRadius: '8px', border: '1px solid',
                ...(draft.cameraType === o.value
                  ? { bgcolor: '#6d28d9', color: '#fff', borderColor: '#6d28d9' }
                  : { bgcolor: '#fff', color: '#475569', borderColor: '#cbd5e1' }) }}>
              {o.label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={!!draft.needsFollowUp} onChange={e => set('needsFollowUp', e.target.checked)}
            sx={{ color: FLAG_COLOR, '&.Mui-checked': { color: FLAG_COLOR } }} />}
          label={<Typography sx={{ fontSize: '13px' }}>Needs follow-up / tech help</Typography>}
        />
        {showReview && (
          <FormControlLabel
            control={<Checkbox checked={!!draft.footageDownloaded} onChange={e => set('footageDownloaded', e.target.checked)}
              sx={{ color: FOOTAGE_DOWNLOADED_COLOR, '&.Mui-checked': { color: FOOTAGE_DOWNLOADED_COLOR } }} />}
            label={<Typography sx={{ fontSize: '13px' }}>Footage downloaded (vs. just viewed)</Typography>}
          />
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>Location photo (optional)</Typography>
        <PhotoUploadField label="photo" photo={draft.photo} onChange={setPointPhoto} maxEdge={1000} quality={0.8} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: 1 }}>
          <Typography sx={{ fontSize: '12px', color: '#64748b' }}>Video Offset (optional)</Typography>
          <Tooltip title="If the DVR clock doesn't match actual time (e.g. it's 6 hours behind), note the offset and whether the DVR is ahead or behind. Precise calculation? Use the DVR Time Converter tool.">
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 14, height: 14, borderRadius: '50%', border: '1.5px solid #94a3b8', color: '#94a3b8',
              fontSize: '10px', fontWeight: 700, cursor: 'default' }}>i</Box>
          </Tooltip>
          <Link to="/dvrtime" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#6d28d9', marginLeft: '4px' }}>
            Open DVR Time Converter ↗
          </Link>
        </Box>
        <TextField size="small" fullWidth placeholder="e.g. 6 hours behind"
          value={draft.videoOffset || ''} onChange={e => set('videoOffset', e.target.value)} />
      </Box>

      <TextField fullWidth size="small" label="Notes" multiline minRows={2} value={draft.notes || ''} onChange={e => set('notes', e.target.value)} sx={{ mt: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button onClick={onSave} variant="contained" sx={{ bgcolor: '#6d28d9', fontWeight: 600, '&:hover': { bgcolor: '#5b21b6' } }}>
          {draft.id ? 'Save changes' : 'Save location'}
        </Button>
        <Button onClick={onCancel} variant="text" sx={{ color: '#64748b' }}>Cancel</Button>
        {onDelete && (
          <Button onClick={onDelete} variant="text" sx={{ color: '#dc2626', ml: 'auto' }}>Delete</Button>
        )}
      </Box>
      {draft.lat != null && (
        <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1.5, fontFamily: "'Courier New', monospace" }}>
          {(+draft.lat).toFixed(6)}, {(+draft.lng).toFixed(6)} — drag the purple pin on the map to adjust
        </Typography>
      )}
    </Paper>
  );
}

// ─── Route entry ────────────────────────────────────────────────────────────

export default function VideoCanvass() {
  const { canvassId } = useParams();
  return (
    <>
      <Navbar pageTitle="Video Canvass Tracker" />
      {canvassId ? <CanvassBoard canvassId={canvassId} /> : <CreateCanvass />}
    </>
  );
}
